import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, Activity, Aperture, ChevronRight } from 'lucide-react';

const COLORS = {
  primary: '#0760ad',
  secondary: '#098a48',
  tertiary: '#118a94',
  white: '#FFFFFF',
  darkText: '#1a365d'
};

const DEFAULT_SERVICES = [
  {
    title: "",
    highlight: "",
    description: "",
    features: [""],
    cta: "",
    icon: "FileText",
    image: "",
    color: COLORS.primary,
    bgClass: "bg-blue-50"
  },
  {
    title: "",
    highlight: "",
    description: "",
    features: [""],
    cta: "",
    icon: "Activity",
    image: "",
    color: COLORS.secondary,
    bgClass: "bg-green-50"
  },
  {
    title: "",
    highlight: "",
    description: "",
    features: [""],
    cta: "",
    icon: "Aperture",
    image: "",
    color: COLORS.tertiary,
    bgClass: "bg-teal-50"
  }
];

const HealthcareServicesGridModal = ({ isOpen, onClose, initialData }) => {
  const [servicesData, setServicesData] = useState({
    heading: "",
    subheading: "",
    services: [...DEFAULT_SERVICES],
    buttonText: "",
    buttonLink: ""
  });

  const [activeTab, setActiveTab] = useState('main');
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentUploadIndex, setCurrentUploadIndex] = useState(null);

  useEffect(() => {
    const fetchCurrentData = async () => {
      if (isOpen && !initialData) {
        try {
          setIsLoading(true);
          const response = await fetch(`${import.meta.env.VITE_API_URL}/home/healthcare-services-grid/`);
          if (response.ok) {
            const data = await response.json();
            setServicesData({
              heading: data.heading || "",
              subheading: data.subheading || "",
              services: data.services || [...DEFAULT_SERVICES],
              buttonText: data.buttonText || "",
              buttonLink: data.buttonLink || "",
            });
          }
        } catch (error) {
          console.error('Error fetching services data:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchCurrentData();
  }, [isOpen, initialData]);

  useEffect(() => {
    if (initialData) {
      setServicesData(initialData);
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setServicesData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (index, field, value) => {
    const updatedServices = [...servicesData.services];
    updatedServices[index] = { ...updatedServices[index], [field]: value };
    setServicesData(prev => ({ ...prev, services: updatedServices }));
  };

  const handleFeatureChange = (serviceIndex, featureIndex, value) => {
    const updatedServices = [...servicesData.services];
    const updatedFeatures = [...updatedServices[serviceIndex].features];
    updatedFeatures[featureIndex] = value;
    updatedServices[serviceIndex] = {
      ...updatedServices[serviceIndex],
      features: updatedFeatures
    };
    setServicesData(prev => ({ ...prev, services: updatedServices }));
  };

  const addService = () => {
    setServicesData(prev => ({
      ...prev,
      services: [...prev.services, { 
        title: "",
        highlight: "",
        description: "",
        features: [""],
        cta: "",
        icon: "FileText",
        image: "",
        color: COLORS.primary,
        bgClass: "bg-blue-50"
      }]
    }));
  };

  const removeService = (index) => {
    const updatedServices = [...servicesData.services];
    updatedServices.splice(index, 1);
    setServicesData(prev => ({ ...prev, services: updatedServices }));
  };

  const addFeature = (serviceIndex) => {
    const updatedServices = [...servicesData.services];
    updatedServices[serviceIndex].features.push("");
    setServicesData(prev => ({ ...prev, services: updatedServices }));
  };

  const removeFeature = (serviceIndex, featureIndex) => {
    const updatedServices = [...servicesData.services];
    updatedServices[serviceIndex].features.splice(featureIndex, 1);
    setServicesData(prev => ({ ...prev, services: updatedServices }));
  };

  const handleImageUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);
    formData.append('category', 'service_images');

    try {
      setIsUploading(true);
      setCurrentUploadIndex(index);
      setUploadProgress(0);

      const response = await axios.post(`${import.meta.env.VITE_API_URL}/images/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        }
      });

      const imageUrl = response.data.image?.url || response.data.image;
      if (imageUrl) {
        handleServiceChange(index, 'image', imageUrl);
      } else {
        throw new Error('Invalid image URL in response');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
      setCurrentUploadIndex(null);
      setUploadProgress(0);
    }
  };

  const handleSubmit = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/home/healthcare-services-grid/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(servicesData)
      });

      if (response.ok) {
        const updatedData = await response.json();
        onClose();
      } else {
        console.error('Failed to update services data');
        alert('Failed to save changes. Please try again.');
      }
    } catch (error) {
      console.error('Error updating services data:', error);
      alert('An error occurred while saving. Please try again.');
    }
  };

  const getIconComponent = (iconName) => {
    switch(iconName) {
      case 'FileText': return <FileText className="w-6 h-6" />;
      case 'Activity': return <Activity className="w-6 h-6" />;
      case 'Aperture': return <Aperture className="w-6 h-6" />;
      default: return <FileText className="w-6 h-6" />;
    }
  };

  if (isLoading) {
    return (
      <dialog className={`modal ${isOpen ? 'modal-open' : ''}`}>
        <div className="modal-box">
          <span className="loading loading-spinner loading-lg"></span>
          <p>Loading healthcare services data...</p>
        </div>
      </dialog>
    );
  }

  return (
    <dialog className={`modal ${isOpen ? 'modal-open' : ''}`}>
      <div className="modal-box w-11/12 max-w-5xl max-h-[90vh] relative">
        <button onClick={onClose} className="btn btn-sm btn-circle absolute right-2 top-2">✕</button>
        <h3 className="font-bold text-lg mb-4">Edit Healthcare Services Grid</h3>
        
        <div className="tabs tabs-boxed mb-6">
          <button 
            className={`tab ${activeTab === 'main' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('main')}
          >
            Main Content
          </button>
          <button 
            className={`tab ${activeTab === 'services' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('services')}
          >
            Services ({servicesData.services.length})
          </button>
        </div>
        
        <div className="overflow-y-auto max-h-[60vh] pr-2">
          {activeTab === 'main' && (
            <div className="space-y-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Heading</span>
                </label>
                <input 
                  type="text" 
                  name="heading" 
                  value={servicesData.heading || ''} 
                  onChange={handleChange} 
                  className="input input-bordered w-full" 
                />
              </div>
              
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Subheading</span>
                </label>
                <textarea 
                  name="subheading" 
                  value={servicesData.subheading || ''} 
                  onChange={handleChange} 
                  className="textarea textarea-bordered w-full h-32" 
                />
              </div>
              
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Button Text</span>
                </label>
                <input 
                  type="text" 
                  name="buttonText" 
                  value={servicesData.buttonText || ''} 
                  onChange={handleChange} 
                  className="input input-bordered w-full" 
                />
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">Button Link</span>
                </label>
                <input 
                  type="text" 
                  name="buttonLink" 
                  value={servicesData.buttonLink || ''} 
                  onChange={handleChange} 
                  className="input input-bordered w-full" 
                />
              </div>
            </div>
          )}
          
          {activeTab === 'services' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h4 className="font-bold">Healthcare Services</h4>
                <button 
                  onClick={addService}
                  className="btn btn-sm btn-primary"
                >
                  Add Service
                </button>
              </div>

              {servicesData.services.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No services added yet. Click "Add Service" to create one.
                </div>
              ) : (
                servicesData.services.map((service, serviceIndex) => (
                  <div key={serviceIndex} className="card bg-base-100 shadow-sm p-4 relative mb-6">
                    <button 
                      onClick={() => removeService(serviceIndex)}
                      className="btn btn-sm btn-circle btn-error absolute -top-2 -right-2 z-10"
                    >
                      ✕
                    </button>
                    
                    <h4 className="font-bold mb-3">Service {serviceIndex + 1}</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text">Title</span>
                        </label>
                        <input 
                          type="text" 
                          value={service?.title || ''} 
                          onChange={(e) => handleServiceChange(serviceIndex, 'title', e.target.value)} 
                          className="input input-bordered w-full" 
                        />
                      </div>
                      
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text">Highlight Text</span>
                        </label>
                        <input 
                          type="text" 
                          value={service?.highlight || ''} 
                          onChange={(e) => handleServiceChange(serviceIndex, 'highlight', e.target.value)} 
                          className="input input-bordered w-full" 
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text">CTA Text</span>
                        </label>
                        <input 
                          type="text" 
                          value={service?.cta || ''} 
                          onChange={(e) => handleServiceChange(serviceIndex, 'cta', e.target.value)} 
                          className="input input-bordered w-full" 
                        />
                      </div>

                      <div className="form-control">
                        <label className="label">
                          <span className="label-text">Icon</span>
                        </label>
                        <select 
                          value={service?.icon || 'FileText'} 
                          onChange={(e) => handleServiceChange(serviceIndex, 'icon', e.target.value)} 
                          className="select select-bordered w-full"
                        >
                          <option value="FileText">Document</option>
                          <option value="Activity">Activity</option>
                          <option value="Aperture">Aperture</option>
                        </select>
                      </div>
                    </div>

                    <div className="form-control mb-4">
                      <label className="label">
                        <span className="label-text">Background Image</span>
                      </label>
                      <div className="flex items-center gap-2">
                        {service?.image && (
                          <div className="avatar">
                            <div className="w-12 rounded">
                              <img src={service.image} alt="Service background" />
                            </div>
                          </div>
                        )}
                        <input 
                          type="file" 
                          accept="image/*"
                          onChange={(e) => handleImageUpload(e, serviceIndex)}
                          className="file-input file-input-bordered file-input-sm w-full max-w-xs"
                          disabled={isUploading && currentUploadIndex === serviceIndex}
                        />
                        {isUploading && currentUploadIndex === serviceIndex && (
                          <progress 
                            className="progress progress-primary w-20" 
                            value={uploadProgress} 
                            max="100"
                          ></progress>
                        )}
                      </div>
                    </div>

                    <div className="form-control mb-4">
                      <label className="label">
                        <span className="label-text">Color</span>
                      </label>
                      <select 
                        value={service?.color || COLORS.primary} 
                        onChange={(e) => handleServiceChange(serviceIndex, 'color', e.target.value)} 
                        className="select select-bordered w-full"
                      >
                        <option value={COLORS.primary}>Primary Blue</option>
                        <option value={COLORS.secondary}>Secondary Green</option>
                        <option value={COLORS.tertiary}>Tertiary Teal</option>
                      </select>
                    </div>
                    
                    <div className="form-control mb-4">
                      <label className="label">
                        <span className="label-text">Description</span>
                      </label>
                      <textarea 
                        value={service?.description || ''} 
                        onChange={(e) => handleServiceChange(serviceIndex, 'description', e.target.value)} 
                        className="textarea textarea-bordered w-full h-24" 
                      />
                    </div>
                    
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text">Features</span>
                        <button 
                          onClick={() => addFeature(serviceIndex)}
                          className="btn btn-xs btn-primary"
                        >
                          Add Feature
                        </button>
                      </label>
                      
                      {service?.features?.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center gap-2 mb-2">
                          <input 
                            type="text" 
                            value={feature || ''} 
                            onChange={(e) => handleFeatureChange(serviceIndex, featureIndex, e.target.value)} 
                            className="input input-bordered w-full" 
                          />
                          <button 
                            onClick={() => removeFeature(serviceIndex, featureIndex)}
                            className="btn btn-circle btn-xs btn-error"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>

                    <div className="mt-4 p-4 rounded-lg bg-gray-50">
                      <h5 className="font-medium mb-2">Preview:</h5>
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg flex items-center justify-center" 
                          style={{ backgroundColor: service.color, color: COLORS.white }}>
                          {getIconComponent(service.icon)}
                        </div>
                        <div>
                          <div className="font-bold">{service.title || "Service Title"}</div>
                          <div className="text-sm" style={{ color: service.color }}>
                            {service.highlight || "Highlight text"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
        
        <div className="modal-action mt-4">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit}>
            Save Changes
          </button>
        </div>
      </div>
    </dialog>
  );
};

export default HealthcareServicesGridModal;